import React, { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowUpRightFromSquare,
  faCheck,
  faChevronDown,
  faChevronUp,
  faCopy,
  faEnvelope,
} from '@fortawesome/free-solid-svg-icons';
import { labelClass } from 'modules/Common/form/v2/primitives/inputClasses';
import { CheckboxField } from 'modules/Common/form/v2';
import CgRadixButton from 'modules/Common/ui/CgRadixButton';
import { useBranding } from 'modules/App/hooks/useBranding';
// This panel is both a RECEIVER (its own live-preview iframe) and the AUTHOR of
// a second, standalone receiver that runs on the customer's own site — both must
// consume the canonical source/event literals postMessageBridge.js owns, never
// hand-rolled copies.
import { EVENTS, SOURCE } from 'modules/Embed/postMessageBridge';
import BrowserFrame from './BrowserFrame';
import WidgetThemePicker from './WidgetThemePicker';
import { buildPublicPortalPath, buildPublicPortalUrl } from '../publicPortalRoutes';

// The surface-agnostic CBAM Portal embed configurator, extracted from the
// free-tier EmbedWidgetPage so the paid operator portal offers the same widget.
// Everything supplier-specific (upsells, MockData, free-tier analytics) stays
// with the caller; this owns the controls, the snippet, and the live preview.
//
// Cosmetic prefs (`layout`, `showFactors`) are CONTROLLED by the caller and stay
// client-only — they are URL params on the widget, never persisted server-side.

const LAYOUTS = [
  { key: 'card', label: 'Card' },
  { key: 'table', label: 'Table' },
  { key: 'compact', label: 'Compact' },
];

const CMS_INSTRUCTIONS = {
  WordPress:
    'In the block editor add a "Custom HTML" block, paste the snippet, then Preview → Publish.',
  Wix: 'Add → Embed code → "Embed HTML", paste the snippet into the code box and click Apply.',
  'Plain HTML': 'Paste the snippet anywhere inside the <body> of the page that should show it.',
};

const WEBMASTER_EMAIL = (snippet, brandName) =>
  `Hi,

Could you add our CBAM Portal widget to the sustainability page? Just paste this HTML where it should appear:

${snippet}

It loads our live emissions data from ${brandName} and sizes itself automatically. Thanks!`;

// V2-field-compatible "field" shim for cosmetic widget preferences that live in
// client-only state, not a react-hook-form tree — CheckboxField only needs
// {value, onChange, name}, so this reuses the real V2 component (D48: never a
// default HTML input) without pulling in a Form/Controller for state that isn't
// submitted.
const manualField = (name, value, onChange) => ({ name, value, onChange, onBlur: () => {} });

// The copied snippet is raw HTML pasted onto the customer's OWN site, so any
// value interpolated into an attribute must be HTML-escaped — a `"` in a company
// name would otherwise break out of the title="" attribute (G3).
export const escapeHtmlAttribute = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

// Exported for the co-located test: the snippet is the actual deliverable of
// this page, and its correctness (absolute URL, no colour, no password,
// deterministic id, escaped title, standalone resize listener) is worth pinning
// without mounting the panel.
export const buildEmbedSnippet = ({ slug, companyName, layout, showFactors, height, idPrefix }) => {
  if (!slug) return '';
  // Slug-derived (not random) so the snippet is deterministic and safe to paste
  // twice for two different portals on the same page.
  const iframeId = `${idPrefix}-${slug}`;
  // The snippet OMITS primaryColor so persisted re-themes reach widgets that
  // are already embedded on customer sites.
  const query = `layout=${layout}&showFactors=${showFactors}`;
  return `<iframe
  id="${iframeId}"
  src="${buildPublicPortalUrl(slug, { surface: 'widget' })}?${query}"
  width="100%" height="${height}" frameborder="0"
  title="${escapeHtmlAttribute(companyName)} CBAM Portal data"></iframe>
<script>
(function () {
  var iframe = document.getElementById('${iframeId}');
  if (!iframe) return;
  window.addEventListener('message', function (e) {
    if (!e.data || e.data.source !== '${SOURCE}' || e.data.type !== '${EVENTS.RESIZE}') return;
    var h = Number(e.data.payload && e.data.payload.height);
    if (h > 0) iframe.style.height = h + 'px';
  });
})();
</script>`;
};

function HowToEmbed({ snippet, previewPath }) {
  const brand = useBranding();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState('WordPress');
  const [copiedEmail, setCopiedEmail] = useState(false);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-transparent border-0 text-left text-xs font-bold uppercase tracking-wider text-slate-500 hover:bg-slate-50 transition-colors"
      >
        How to put this on your website
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          className="text-slate-400"
        />
      </button>
      {open && (
        <div className="px-4 pb-4 flex flex-col gap-3">
          <ol className="m-0 pl-4 text-xs text-slate-600 flex flex-col gap-1">
            <li>Copy the embed code above.</li>
            <li>Paste it into your website where the widget should appear.</li>
            <li>
              Done — the widget shows your live CBAM Portal data and auto-sizes itself (it reports
              its height to your page via <code className="text-[10px]">cg-embed:resize</code>).
            </li>
          </ol>

          <div>
            <div className="flex gap-2 mb-2">
              {Object.keys(CMS_INSTRUCTIONS).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setTab(k)}
                  className={`text-[11px] font-medium rounded-lg px-2 py-1 border transition-colors ${
                    tab === k
                      ? 'bg-primaryColor/10 text-primaryColor border-primaryColor/30'
                      : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  {k}
                </button>
              ))}
            </div>
            <p className="text-xs text-slate-600 m-0">{CMS_INSTRUCTIONS[tab]}</p>
          </div>

          <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <a
              href={previewPath}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-primaryColor no-underline hover:underline"
            >
              <FontAwesomeIcon
                icon={faArrowUpRightFromSquare}
                className="text-[10px]"
              />
              Open the widget in a new tab
            </a>
            <span className="flex-1" />
            <CgRadixButton
              variant="outline"
              size="sm"
              icon={copiedEmail ? faCheck : faEnvelope}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(WEBMASTER_EMAIL(snippet, brand.name));
                } catch {
                  /* clipboard unavailable in demo env */
                }
                setCopiedEmail(true);
                setTimeout(() => setCopiedEmail(false), 2000);
              }}
            >
              {copiedEmail ? 'Email copied!' : 'Copy email for your webmaster'}
            </CgRadixButton>
          </div>
        </div>
      )}
    </div>
  );
}

export default function EmbedSnippetPanel({
  slug,
  companyName,
  effectiveColor,
  persistColor,
  colorSaving = false,
  prefs,
  onPrefsChange,
  onCopied,
  idPrefix = 'cg-portal-widget',
  children,
}) {
  const [copied, setCopied] = useState(false);
  const [frameHeight, setFrameHeight] = useState(420);

  const layout = prefs?.layout || 'card';
  const showFactors = prefs?.showFactors ?? true;

  // The live preview reflects the PERSISTED colour via the URL param (top of the
  // widget's theme precedence) and reloads when it changes (the iframe is keyed
  // on this path). The copyable snippet omits it — see buildEmbedSnippet.
  const previewQuery = `primaryColor=${String(effectiveColor || '').replace('#', '')}&layout=${layout}&showFactors=${showFactors}`;
  const previewPath = slug
    ? `${buildPublicPortalPath(slug, { surface: 'widget' })}?${previewQuery}`
    : '';
  const snippet = buildEmbedSnippet({
    slug,
    companyName,
    layout,
    showFactors,
    height: frameHeight,
    idPrefix,
  });

  // Listen for the widget's cg-embed:resize messages — the same auto-sizing
  // contract the copied snippet ships to host pages (which never load this app).
  useEffect(() => {
    const onMessage = (e) => {
      if (e.data?.source === SOURCE && e.data.type === EVENTS.RESIZE) {
        const h = Number(e.data.payload?.height);
        if (h > 0) setFrameHeight(Math.ceil(h));
      }
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const copySnippet = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
    } catch {
      /* clipboard unavailable — still mark the step done */
    }
    setCopied(true);
    onCopied?.({ layout, theme: effectiveColor });
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    // No grid-cols-1: the prebuilt CSS bundle's .grid-cols-1 loads after the JIT
    // rule and would override the lg: arbitrary-value variant. A grid defaults
    // to one column anyway — BUT that single implicit column still sizes to its
    // widest child's max-content, not the container, so a wide preview forced
    // this grid past the viewport below `lg`. `grid-cols-[minmax(0,1fr)]` is a
    // distinct arbitrary-value selector, so it sidesteps the override bug while
    // actually constraining the column.
    <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[320px_1fr] gap-6 items-start">
      {/* Config column */}
      <div className="rounded-2xl bg-white border border-slate-200 p-5 flex flex-col gap-5">
        <div>
          <span className={labelClass}>Theme colour</span>
          <p className="text-[11px] text-slate-400 m-0 mt-0.5 mb-2">
            Applies to your public CBAM Portal page and this widget.
          </p>
          <WidgetThemePicker
            value={effectiveColor}
            saving={colorSaving}
            onApply={persistColor}
          />
        </div>

        <div>
          <span className={labelClass}>Layout</span>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {LAYOUTS.map((l) => (
              <button
                key={l.key}
                type="button"
                onClick={() => onPrefsChange?.({ layout: l.key })}
                className={`text-xs font-medium rounded-xl px-2 py-2 border transition-colors ${
                  layout === l.key
                    ? 'bg-primaryColor/10 text-primaryColor border-primaryColor/30'
                    : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex flex-col gap-2.5">
          <CheckboxField
            id="widget-show-factors"
            field={manualField('showFactors', showFactors, (checked) =>
              onPrefsChange?.({ showFactors: checked }),
            )}
            inlineLabel="Show emissions data"
          />
        </div>

        <div className="border-t border-slate-100 pt-4">
          <span className={labelClass}>Embed code</span>
          <pre className="text-[10px] leading-relaxed text-slate-600 bg-slate-50 border border-slate-200 rounded-xl p-3 overflow-x-auto whitespace-pre-wrap break-all m-0 mt-1">
            {snippet}
          </pre>
          <CgRadixButton
            variant={copied ? 'outline' : 'primary'}
            icon={copied ? faCheck : faCopy}
            className="mt-2 w-full justify-center"
            onClick={copySnippet}
          >
            {copied ? 'Copied to clipboard!' : 'Copy snippet'}
          </CgRadixButton>
        </div>
      </div>

      {/* Preview column — a REAL iframe of the public widget route */}
      <div className="flex flex-col gap-3">
        <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 m-0">
          Live preview — this is the actual iframe your site will load
        </p>
        <BrowserFrame url={`https://www.${(slug || '').replace(/-/g, '')}.com/sustainability`}>
          <iframe
            key={previewPath}
            src={previewPath}
            title={`${companyName} CBAM Portal widget`}
            className="w-full border-0 block bg-slate-50/60"
            style={{ height: frameHeight }}
          />
        </BrowserFrame>
        <HowToEmbed
          snippet={snippet}
          previewPath={previewPath}
        />
        {children}
      </div>
    </div>
  );
}
